import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate } from 'react-router-dom';
import openAIVoiceService from './openAIVoiceService';
import { AIVA_CONFIG, API_ENDPOINTS } from './config';

function App() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [userInput, setUserInput] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [error, setError] = useState('');
  
  // Avatar creation redirect state
  const [showAvatarInstructions, setShowAvatarInstructions] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [visitorId, setVisitorId] = useState(null);
  
  // User info state - conversational flow
  const [userInfo, setUserInfo] = useState({
    name: '',
    mobile: '',
    conversationStep: 'name', // name -> mobile -> complete
  });

  // Responsive detection
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 480);
      setIsTablet(width > 480 && width <= 1024);
    };
    
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Set up event handlers
  useEffect(() => {
    openAIVoiceService.onStatusChange = (newStatus) => {
      console.log('Voice service status:', newStatus);
      setStatus(newStatus);
      
      if (newStatus === 'listening') {
        setIsListening(true);
        setIsSpeaking(false);
      } else if (newStatus === 'speaking') {
        setIsListening(false);
        setIsSpeaking(true);
      } else if (newStatus === 'connected') {
        setIsInitialized(true);
      } else if (newStatus === 'idle') {
        setIsListening(false);
        setIsSpeaking(false);
      }
    };

    openAIVoiceService.onAiResponse = (text, type) => {
      if (type === 'complete') {
        setMessage(text);
      }
    };

    openAIVoiceService.onError = (errorMessage) => {
      console.error('Voice service error:', errorMessage);
      setError(errorMessage.message || errorMessage);
    };

    // Set the speech end callback to auto-start listening
    openAIVoiceService.setOnSpeechEnd(() => {
      console.log('Speech ended, starting to listen...');
      if (!openAIVoiceService.isCurrentlyListening()) {
        openAIVoiceService.startListening();
      }
    });
  }, []);

  // Speak instructions when avatar modal opens
  useEffect(() => {
    if (showAvatarInstructions && userInfo.name) {
      const instructions = `Great news, ${userInfo.name}! Let's create your AI avatar based on your photo. Here are the steps: First, position your face in front of the camera. Second, make sure your face is clearly visible. Third, capture the image and let AI create your avatar.`;
      openAIVoiceService.speak(instructions);
    }
  }, [showAvatarInstructions, userInfo.name]);

  // Extract name from user response
  const extractName = (response) => {
    const namePatterns = [
      /my name is (.+)/i,
      /i'm (.+)/i,
      /i am (.+)/i,
      /this is (.+)/i,
      /call me (.+)/i,
      /it's (.+)/i,
      /its (.+)/i,
    ];
    
    for (const pattern of namePatterns) {
      const match = response.match(pattern);
      if (match) {
        return match[1].trim().split(' ')[0].replace(/[^a-zA-Z]/g, '');
      }
    }
    
    const words = response.trim().split(' ');
    if (words.length > 0) {
      return words[0].replace(/[^a-zA-Z]/g, '');
    }
    
    return response.trim();
  };

  // Validate name
  const validateName = (name) => {
    if (!name || name.length < 2) {
      return { valid: false, message: "Please provide a valid name with at least 2 characters." };
    }
    // Check if name contains only letters and spaces
    if (!/^[a-zA-Z\s]+$/.test(name)) {
      return { valid: false, message: "Please provide a valid name using only letters." };
    }
    return { valid: true };
  };

  // Validate mobile number
  const validateMobile = (mobile) => {
    // Remove any spaces or special characters
    const cleaned = mobile.replace(/\s/g, '');
    // Check if it's a valid Indian mobile number (10 digits, starting with 6-9)
    const mobilePattern = /^[6-9]\d{9}$/;
    if (!mobilePattern.test(cleaned)) {
      return { valid: false, message: "Please provide a valid 10-digit mobile number." };
    }
    return { valid: true };
  };

  // Generate next question using OpenAI
  const generateNextQuestion = useCallback(async (currentInfo) => {
    const prompt = `You are AIVA, an AI assistant for India AI Impact Summit 2026. 
    
Current user info:
- Name: ${currentInfo.name || 'Not provided'}
- Company: ${currentInfo.company || 'Not provided'}
- Role: ${currentInfo.role || 'Not provided'}
- AI Excitement: ${currentInfo.aiExcitement || 'Not provided'}
- Business Challenges: ${currentInfo.businessChallenges || 'Not provided'}

Based on what's missing, ask the next natural conversational question to learn more about the user. 
Keep it brief (1 sentence) and conversational.

If name is missing, ask for their name.
If company is missing, ask about their company.
If role is missing, ask about their role.
If challenges are missing, ask about business challenges.
If everything is collected, ask how you can help them at the summit.`;

    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.REACT_APP_OPENAI_API_KEY || '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'}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4',
          messages: [
            {
              role: 'system',
              content: 'You are a friendly AI assistant. Keep responses brief and conversational.',
            },
            {
              role: 'user',
              content: prompt,
            },
          ],
          max_tokens: 100,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get question from OpenAI');
      }

      const data = await response.json();
      return data.choices[0]?.message?.content || 'How can I help you today?';
    } catch (error) {
      console.error('Error generating question:', error);
      // Fallback questions based on what's missing
      if (!currentInfo.mobile) {
        return `And what is your mobile number, ${currentInfo.name}?`;
      }
      return 'How can I help you today?';
    }
  }, []);


  // Process user response with validation
  const processUserResponse = useCallback(async (response) => {
    console.log('Processing response:', response);
    
    // Extract name if first question
    if (userInfo.conversationStep === 'name') {
      const name = extractName(response);
      const validation = validateName(name);
      
      if (!validation.valid) {
        // Ask again with validation message
        const retryMessage = validation.message + " Could you please tell me your name?";
        setMessage(retryMessage);
        openAIVoiceService.speak(retryMessage);
        return;
      }
      
      const newInfo = { ...userInfo, name, conversationStep: 'mobile' };
      setUserInfo(newInfo);
      
      // Ask next question for mobile number
      const question = await generateNextQuestion(newInfo);
      setMessage(question);
      openAIVoiceService.speak(question);
    } else if (userInfo.conversationStep === 'mobile') {
      // Extract and validate mobile number
      const mobile = response.replace(/\s/g, '');
      const validation = validateMobile(mobile);
      
      if (!validation.valid) {
        // Ask again with validation message
        const retryMessage = validation.message + " Could you please tell me your 10-digit mobile number?";
        setMessage(retryMessage);
        openAIVoiceService.speak(retryMessage);
        return;
      }
      
      const newInfo = { ...userInfo, mobile, conversationStep: 'complete' };
      setUserInfo(newInfo);
      
      // Call generateTagline API
      if (sessionId) {
        try {
          console.log('Generating tagline for session:', sessionId);
          fetch(API_ENDPOINTS.generateTagline, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'session-id': sessionId
            },
            body: JSON.stringify({
              name: newInfo.name,
              phone: `+91${mobile}`
            })
          }).then(res => res.json())
            .then(data => {
              console.log('Tagline generation response:', data);
              if (data.data && data.data.visitor_id) {
                setVisitorId(data.data.visitor_id);
                localStorage.setItem('visitor_id', data.data.visitor_id);
              }
            })
            .catch(err => console.error('Tagline generation error:', err));
        } catch (error) {
          console.error('Error calling tagline API:', error);
        }
      }
      
      // Complete the conversation
      const thanksMessage = `Thank you so much, ${newInfo.name}! Your mobile number ${newInfo.mobile} has been recorded. I'm here to help you make the most of the India AI Impact Summit 2026. How can I assist you today?`;
      setMessage(thanksMessage);
      openAIVoiceService.speak(thanksMessage);
      
      // Show avatar creation instructions after a delay
      setTimeout(() => {
        setShowAvatarInstructions(true);
      }, 4000);
    }
  }, [userInfo, generateNextQuestion, sessionId]);

  // Override the transcript update
  useEffect(() => {
    openAIVoiceService.onTranscriptUpdate = (transcript, role) => {
      if (role === 'user') {
        setUserInput(transcript);
        processUserResponse(transcript);
      }
    };
  }, [processUserResponse]);

  // Start the conversation
  const startConversation = useCallback(() => {
    setUserInfo(prev => ({ ...prev, conversationStep: 'name' }));
    
    const welcomeMessage = "Hello! Welcome to the India AI Impact Summit 2026. I'm AIVA, your AI assistant. To get started, could you please tell me your name?";
    setMessage(welcomeMessage);
    openAIVoiceService.speak(welcomeMessage);
  }, []);

  // Handle user interaction
  const handleUserSpeaking = useCallback(async () => {
    console.log('Handle user speaking clicked, status:', status, 'isInitialized:', isInitialized, 'message:', message, 'conversationStep:', userInfo.conversationStep);
    
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setError('Speech recognition not supported in this browser. Please use Chrome or Edge.');
      return;
    }

    // If we already have a conversation in progress (message exists and not complete), repeat the current question
    if (isInitialized && message && userInfo.conversationStep !== 'complete' && status !== 'initializing') {
      console.log('Repeating current question:', message);
      openAIVoiceService.speak(message);
      // Start listening after the question is spoken
      setTimeout(() => {
        openAIVoiceService.startListening();
      }, 2000);
      return;
    }

    if (status === 'idle' || status === 'error' || !isInitialized) {
      setStatus('initializing');
      setError('');
      
      try {
        // Create session before initialization
        try {
          const sessionRes = await fetch(API_ENDPOINTS.sessionsCreate, {
            method: 'POST',
            headers: {
              'accept': 'application/json',
            },
          });
          if (sessionRes.ok) {
            const sessionData = await sessionRes.json();
            if (sessionData.data && sessionData.data.session_id) {
              setSessionId(sessionData.data.session_id);
              localStorage.setItem('session-id', sessionData.data.session_id);
              console.log('Session created successfully:', sessionData.data.session_id);
            }
          }
        } catch (sessionError) {
          console.error('Failed to create session:', sessionError);
        }

        await openAIVoiceService.initialize();
        setIsInitialized(true);
        
        setTimeout(() => {
          startConversation();
        }, 500);
        
      } catch (err) {
        console.error('Initialization error:', err);
        setError('Failed to initialize voice service. Please allow microphone access.');
        setStatus('error');
      }
    } else if (isListening) {
      openAIVoiceService.stopListening();
    } else if (!isSpeaking) {
      openAIVoiceService.startListening();
    }
  }, [status, isInitialized, isListening, isSpeaking, startConversation, message, userInfo.conversationStep]);

  // Get status text
  const getStatusText = () => {
    switch (status) {
      case 'initializing':
        return 'Initializing...';
      case 'listening':
        return 'Listening...';
      case 'speaking':
        return 'AIVA Speaking...';
      case 'thinking':
        return 'Processing...';
      case 'error':
        return 'Error - Tap to Retry';
      default:
        return 'Tap to Speak';
    }
  };

  // Get personalized greeting
  const getPersonalizedGreeting = () => {
    if (userInfo.conversationStep === 'complete' && userInfo.name) {
      return `Great to see you, ${userInfo.name}!`;
    }
    return null;
  };

  // Proceed to avatar creation
  const proceedToAvatarCreation = () => {
    setIsRedirecting(true);
    navigate('/avatar', { state: { userInfo, sessionId, visitorId } });
  };

  // Get current question prompt
  const getCurrentPrompt = () => {
    if (userInfo.conversationStep === 'complete') {
      return "How can I help you today?";
    }
    return userInfo.name ? "Please answer the question above" : "Please tell me your name";
  };

  return (
    <div className="aiva-app">
      {/* AI Background Layer */}
      <div className="ai-background">
        {/* Neural Network Grid */}
        <div className="neural-grid"></div>
        
        {/* AI Pulse Waves */}
        {!isMobile && <div className="ai-pulse"></div>}
        {!isMobile && <div className="ai-pulse"></div>}
        <div className="ai-pulse"></div>
        
        {/* Neural Network Nodes */}
        {Array.from({ length: isMobile ? 10 : isTablet ? 20 : 30 }).map((_, i) => (
          <div 
            key={`node-${i}`} 
            className="neural-node"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`
            }}
          />
        ))}
        
        {/* Neural Connections */}
        {!isMobile && Array.from({ length: isTablet ? 5 : 8 }).map((_, i) => (
          <div 
            key={`connection-${i}`}
            className="neural-connection"
            style={{
              top: `${Math.random() * 80 + 10}%`,
              left: `${Math.random() * 60}%`,
              width: `${Math.random() * 200 + 100}px`,
              transform: `rotate(${Math.random() * 30 - 15}deg)`,
              animationDelay: `${Math.random() * 4}s`
            }}
          />
        ))}
        
        {/* Circuit Pattern Lines */}
        {!isMobile && (
          <div className="circuit-layer">
            {Array.from({ length: isTablet ? 3 : 5 }).map((_, i) => (
              <React.Fragment key={`circuit-${i}`}>
                <div 
                  className="circuit-line horizontal"
                  style={{
                    width: '100%',
                    top: `${15 + i * 18}%`,
                    left: 0,
                    animationDelay: `${i * 0.5}s`
                  }}
                />
                <div 
                  className="circuit-line vertical"
                  style={{
                    height: '100%',
                    left: `${10 + i * 20}%`,
                    top: 0,
                    animationDelay: `${i * 0.5 + 0.75}s`
                  }}
                />
              </React.Fragment>
            ))}
          </div>
        )}
        
        {/* NLP Data Particles */}
        {['NLP', 'AI', 'voice', 'speech', 'text', 'data'].map((word, i) => (
          <div 
            key={`word-${i}`}
            className="nlp-word"
            style={{
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 20}s`,
              animationDuration: `${15 + Math.random() * 10}s`
            }}
          >
            {word}
          </div>
        ))}
        
        {/* Floating Data Particles */}
        {Array.from({ length: isMobile ? 5 : isTablet ? 10 : 15 }).map((_, i) => (
          <div 
            key={`particle-${i}`}
            className="data-particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 15}s`,
              animationDuration: `${10 + Math.random() * 10}s`
            }}
          >
            {Math.random() > 0.5 ? '01' : '10'}
          </div>
        ))}
        
        {/* Orb Effects */}
        <div className="orb orb-1"></div>
        <div className="orb orb-2"></div>
        <div className="orb orb-3"></div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Avatar Section */}
        <div className="avatar-container">
          <div className="avatar-wrapper">
            {/* Glow Effect */}
            <div className={`avatar-glow ${isListening ? 'listening' : ''} ${isSpeaking ? 'speaking' : ''}`}></div>
            
            {/* Rotating Rings */}
            <div className="ring-container">
              <div className="ring ring-1"></div>
              <div className="ring ring-2"></div>
              <div className="ring ring-3"></div>
            </div>

            {/* Avatar */}
            <div 
              className={`avatar-image-container ${isSpeaking ? 'speaking' : ''} ${isListening ? 'listening' : ''}`}
              onClick={handleUserSpeaking}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleUserSpeaking()}
            >
              <img src="/AI.png" alt="AIVA Avatar" className="avatar-image" />
              {isSpeaking && <div className="lip-sync-overlay"></div>}
            </div>

            {/* Status Indicator */}
            <div className="status-indicator" onClick={handleUserSpeaking} style={{ cursor: 'pointer' }}>
              <div className={`status-dot ${isListening ? 'listening' : ''} ${isSpeaking ? 'speaking' : ''}`}></div>
              <span className="status-text">{getStatusText()}</span>
            </div>
          </div>
        </div>

        {/* Greeting Section */}
        <div className="greeting-section">
          <h1 className="greeting-title">Hello, I'm AIVA</h1>
          <p className="greeting-subtitle">
            {isInitialized 
              ? getPersonalizedGreeting() || "I'm ready to assist you!" 
              : "Tap the button below to enable audio and start our conversation!"}
          </p>
          {isInitialized && (
            <p className="current-prompt" style={{ fontSize: '0.9rem', color: 'var(--primary-light)', marginTop: '10px' }}>
              🎤 {getCurrentPrompt()}
            </p>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="glass-card" style={{ background: 'rgba(239, 68, 68, 0.2)', borderColor: 'rgba(239, 68, 68, 0.3)' }}>
            <p className="message-text" style={{ textAlign: 'center', color: '#ef4444' }}>
              ⚠️ {error}
            </p>
          </div>
        )}

        {/* AI Message Bubble */}
        {message && (
          <div className="glass-card">
            <div className="message-bubble">
              <div className="message-avatar">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 3a3 3 0 1 1 0 6 3 3 0 0 1 0-6zm0 14.2a7.2 7.2 0 0 1-6-3.2c.03-2 4-3.1 6-3.1s5.97 1.1 6 3.1a7.2 7.2 0 0 1-6 3.2z"/>
                </svg>
              </div>
              <div className="message-content">
                <p className="message-text" style={{ whiteSpace: 'pre-wrap' }}>{message}</p>
                {userInput && (
                  <p className="message-text" style={{ marginTop: '12px', fontStyle: 'italic', color: 'var(--text-muted)' }}>
                    👤 You said: "{userInput}"
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* User Info Display */}
        {userInfo.name && (
          <div className="glass-card" style={{ marginTop: '10px', padding: '15px' }}>
            <p className="message-text" style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
              📋 <strong>Collected Info:</strong>
              {userInfo.name && ` 👤 Name: ${userInfo.name}`}
              {userInfo.company && ` 🏢 Company: ${userInfo.company}`}
              {userInfo.role && ` 💼 Role: ${userInfo.role}`}
              {userInfo.aiExcitement && ` 🤖 AI: ${userInfo.aiExcitement.substring(0, 30)}...`}
              {userInfo.businessChallenges && ` 📊 Challenges: ${userInfo.businessChallenges.substring(0, 30)}...`}
            </p>
          </div>
        )}
      </div>

      {/* Avatar Creation Instructions Modal */}
      {showAvatarInstructions && (
        <div className="avatar-instructions-modal-overlay">
          <div className="avatar-instructions-modal">
            <div className="modal-icon">📸</div>
            <h2>Create Your Personalized Avatar</h2>
            <p>
              Great news, {userInfo.name}! Let's create your AI avatar based on your photo.
            </p>
            <div className="instructions-steps">
              <div className="step">
                <span className="step-number">1</span>
                <span>Position your face in front of the camera</span>
              </div>
              <div className="step">
                <span className="step-number">2</span>
                <span>Make sure your face is clearly visible</span>
              </div>
              <div className="step">
                <span className="step-number">3</span>
                <span>Capture the image and let AI create your avatar</span>
              </div>
            </div>
            <button 
              className="proceed-btn"
              onClick={proceedToAvatarCreation}
              disabled={isRedirecting}
            >
              {isRedirecting ? 'Opening Camera...' : '📷 Proceed to Camera'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
